import React, { useEffect, useRef, useState } from "react";
import { Col, Image, Modal, Row, Spinner, Toast, ToastContainer } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { BsCamera } from "react-icons/bs";
import { useLocation, useNavigate } from "react-router-dom";
import { IUserData, IUserProfileController } from "../../../../core";
import userNoImage from "../../../../dist/images/users/no-image.svg";
import { FormCodeValidateComponent } from "../../../../modules/account/site/impl/components/form-code-validate.component.tsx";
import { useAuth } from "../../../../providers/AuthContext.tsx";
import { UserDataForm } from "./user-data-form.component.tsx";
import { userDataPanelFields } from "./user-data-panel-fields.ts";

interface IUserDataPanel {
   controller: IUserProfileController;
   showModal: boolean;
   setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export const UserDataPanel: React.FC<IUserDataPanel> = ({ controller, showModal, setShowModal }) => {
   const { setCallbackLogin, handleLogin, handleLogout, userData } = useAuth();
   const navigate = useNavigate();
   const location = useLocation();
   const { t } = useTranslation('core');

   const [isLoading, setIsLoading] = useState(false);
   const [isLoadingImage, setIsLoadingImage] = useState(false);
   const [validated, setValidated] = useState(false);
   const [invalidFields, setInvalidFields] = useState<Array<string>>(['']);
   const [selectedImage, setSelectedImage] = useState<File[]>([]);
   const [showCodeValidateForm, setShowCodeValidateForm] = useState<boolean>(false);
   const [typeResponse, setTypeResponse] = useState<string>('SMS');
   const [receiverResponse, setReceiverResponse] = useState<string>('****');
   const [stepFormCode, setStepFormCode] = useState<number>(1);
   const [toastingText, setToastingText] = useState<string | null>(null);
   const [toastingType, setToastingType] = useState<string>("success");

   let formFields: IUserData = userDataPanelFields;

   if (!userData.subscriber) {
      formFields = Object.entries(formFields)
         .filter(([key, value]) => !value.subscriber)
         .reduce((acc, [key, value]) => {
            acc[key] = value || "";
            return acc;
         }, {} as IUserData);
   }

   const initialFormData = Object.keys(formFields).reduce((acc, key) => {
      acc[key] = "";
      return acc;
   }, {} as IUserData);

   const [formData, setFormData] = useState<IUserData>({
      ...initialFormData,
      image: userNoImage
   });

   const {
      image
   } = formData;

   const hiddenFileInput = useRef<HTMLInputElement>(null);

   const validateNewUserInfo = ({ response }) => {
      setStepFormCode(3);
      setTypeResponse(response.confirm['messageType']);
      setReceiverResponse(response.confirm['receiver']);
   }

   const completeProcess = ({ response }) => {
      if (response.response === "success") {
         setIsLoading(true);
         setShowCodeValidateForm(false);
         setTimeout(function () {
            setShowModal(false);
         }, 5000);
         setToastingType("success");
         setToastingText(t('profileUpdatedSuccessfully'));
      } else {
         setToastingType("danger");
         setToastingText(t(response.error));
      }
   }

   const fetchData = async () => {
      if (showModal) {
         try {
            const response = await controller.getUserProfileData();
            if (response && response.success === true && response.response) {
               setFormData(response.response);
            } else {
               setShowModal(false);
            }
            setIsLoading(false);
         } catch (error) {
            if (error === "401") {
               setCallbackLogin(
                  {
                     goBack: true,
                     origin: location.pathname,
                  }
               );
               handleLogout();
               navigate('/auth');
            }
         }
      }
   }

   useEffect(() => {
      fetchData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [showModal])

   const handleClick = event => {
      setIsLoadingImage(true);
      if (hiddenFileInput.current) {
         hiddenFileInput.current.click();
      }
   };

   const handleChangeImage = (event) => {
      if (event.target.files && event.target.files.length > 0) {
         const fileUploaded = event.target.files;
         setSelectedImage(fileUploaded);

         const file = fileUploaded[0];
         if (file) {
            const preview = URL.createObjectURL(file);
            setFormData((prevData) => ({
               ...prevData,
               image: preview,
            }));
         }
      }

      setTimeout(() => setIsLoadingImage(false), 500);
   };


   const handleFormSubmit = async (e) => {
      setIsLoading(true);
      e.preventDefault();
      const form = e.currentTarget;
      if (form.checkValidity() === false) {
         setValidated(true);
         setIsLoading(false);
         e.stopPropagation();
         return false;
      }

      const submitedFormData = new FormData();
      Object.entries(formFields).map(([key]) => (
         submitedFormData.append(key, formData[key])
      ))

      submitedFormData.append(`image`, selectedImage[0]);

      try {
         const response = await controller.updateUserProfile(submitedFormData);
         if (response && response.success === true) {
            if (response.response === 'CONFIRM' && response.confirm) {
               setTypeResponse(response.confirm['messageType']);
               setReceiverResponse(response.confirm['receiver']);
               setShowCodeValidateForm(true);
            } else {
               await handleLogin(response.response);
               setShowModal(false);
            }
         } else if (!response.success && response!.error) {
            localStorage.setItem('global-toast', JSON.stringify({ type: 'error', message: t('requestError') }));
            setInvalidFields(response.error);
         }
         setIsLoading(false);
      } catch (error) {
         if (error === "401") {
            setCallbackLogin(
               {
                  goBack: true,
                  origin: location.pathname,
               }
            );
            handleLogout();
            navigate('/auth');
         }
      }
   };

   const closeUserProfilePanel = () => {
      setShowModal(false);
   }

   return (
      <>
         <Modal
            size={"lg"}
            show={showModal}
            onHide={() => closeUserProfilePanel()}
         >
            <Modal.Body>
               {!showCodeValidateForm ?
                  <Row className={"mt-4 mb-4"}>
                     <Col xs={12} sm={12} md={12} lg={4} className="d-flex justify-content-center" style={{ height: 200, cursor: "pointer" }}>
                        <Row className={"mt-5"}>
                           <Col onClick={handleClick} className="d-flex justify-content-center" xs={12}>
                              <Image src={image ? image : userNoImage} roundedCircle width={110} height={110} className="object-fit-cover border" />
                              <input
                                 type="file"
                                 onChange={handleChangeImage}
                                 onBlur={() => setIsLoadingImage(false)}
                                 ref={hiddenFileInput}
                                 style={{ display: 'none' }}
                              />
                              <div className="d-flex justify-content-center" style={{ position: "absolute", marginTop: 80 }}>
                                 {isLoadingImage ? <Spinner variant="warning" size="sm" /> : <BsCamera color="#ffffff" />}
                              </div>
                           </Col>
                           <Col className="d-flex justify-content-center" xs={12}>
                              <p className={"text-capitalize text-center"}>{formData.fullname}</p>
                           </Col>
                        </Row>
                     </Col>
                     <UserDataForm
                        handleFormSubmit={handleFormSubmit}
                        formValidate={validated}
                        setIsLoading={setIsLoading}
                        isLoading={isLoading}
                        setFormData={setFormData}
                        formData={formData}
                        invalidFields={invalidFields}
                        formFields={formFields}
                     />
                  </Row>
                  :
                  <Row className={"mt-4"}>
                     <Col>
                        <h4 className="mb-2 text-center">{t('twoFactors')}</h4>
                        <FormCodeValidateComponent
                           step={stepFormCode}
                           subtitle={t(`securityCode${typeResponse}Info`, { receiver: receiverResponse })}
                           feedback={
                              stepFormCode === 1 ? validateNewUserInfo :
                                 stepFormCode === 3 ? completeProcess : (a) => { }
                           }
                           onClose={() => setShowCodeValidateForm(false)}
                        />
                     </Col>
                  </Row>
               }
            </Modal.Body>
         </Modal>

         <ToastContainer position="top-end">
            <Toast
               bg={toastingType}
               onClose={() => setToastingText(null)}
               show={toastingText !== null}
               delay={5000}
               autohide
            >
               <Toast.Body className="text-white">{toastingText}</Toast.Body>
            </Toast>
         </ToastContainer>
      </>
   );
}
